import apiClient from "./config/axios";

/**
 * Products API module
 * Handles all product-related API calls
 */
export const productsApi = {
    /**
     * Get products with filters and pagination
     * @param {object} params - Query parameters
     * @param {number} params.page - Page number
     * @param {number} params.limit - Items per page
     * @param {string} params.sortBy - Sort field
     * @param {string} params.categoryIds - Comma-separated category IDs
     * @param {string} params.brandIds - Comma-separated brand IDs
     * @param {string} params.sizeIds - Comma-separated size IDs
     * @param {string} params.colorIds - Comma-separated color IDs
     * @param {string} params.materialIds - Comma-separated material IDs
     * @param {number} params.minPrice - Minimum price
     * @param {number} params.maxPrice - Maximum price
     * @param {string} params.genderId - Gender ID
     * @param {string} params.collectionId - Collection ID
     * @param {string} params.search - Search query
     * @returns {Promise<{success: boolean, data?: array, pagination?: object, message?: string}>}
     */
    getProducts: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();

            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    queryParams.append(key, value);
                }
            });

            const response = await apiClient.get(
                `/api/frontend/products?${queryParams.toString()}`
            );
            // Return the full response data including pagination
            return {
                success: response.data.success,
                data: response.data.data,
                pagination: response.data.pagination,
                message: response.data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                pagination: { hasNextPage: false, totalCount: 0 },
                message:
                    error.response?.data?.message || "Failed to fetch products",
            };
        }
    },

    /**
     * Get subcategory products (optimized for specific subcategory)
     * @param {object} params - Query parameters
     * @param {number} params.mainCategoryId - Main category ID (required)
     * @param {number} params.subCategoryId - Subcategory ID (required)
     * @param {number} params.page - Page number
     * @param {number} params.limit - Items per page
     * @param {string} params.sortBy - Sort field
     * @param {string} params.brandIds - Comma-separated brand IDs
     * @param {string} params.sizeIds - Comma-separated size IDs
     * @param {string} params.colorIds - Comma-separated color IDs
     * @param {string} params.materialIds - Comma-separated material IDs
     * @param {number} params.minPrice - Minimum price
     * @param {number} params.maxPrice - Maximum price
     * @param {string} params.genderId - Gender ID
     * @param {string} params.collectionId - Collection ID
     * @param {string} params.search - Search query
     * @returns {Promise<{success: boolean, data?: array, pagination?: object, message?: string}>}
     */
    getSubCategoryProducts: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();

            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    queryParams.append(key, value);
                }
            });

            const response = await apiClient.get(
                `/api/frontend/subcategory-products?${queryParams.toString()}`
            );
            return {
                success: response.data.success,
                data: response.data.data,
                pagination: response.data.pagination,
                message: response.data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                pagination: { hasMore: false, totalCount: 0 },
                message:
                    error.response?.data?.message || "Failed to fetch products",
            };
        }
    },

    /**
     * Get product by slug
     * @param {string} slug - Product slug
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProductBySlug: async (slug) => {
        try {
            const response = await apiClient.get(
                `/api/get-product-by-slug/${slug}`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to fetch product",
            };
        }
    },

    /**
     * Get product color variants
     * @param {string} slug - Product slug
     * @param {number} colorId - Color ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProductColorVariants: async (slug, colorId) => {
        try {
            const response = await apiClient.get(
                `/api/get-product-color-variants/${slug}/${colorId}`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch color variants",
            };
        }
    },

    /**
     * Get product delivery types
     * @param {number} productId - Product ID
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getProductDeliveryTypes: async (productId) => {
        try {
            const response = await apiClient.get(
                `/api/products/${productId}/delivery-types`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch product delivery types",
            };
        }
    },

    /**
     * Get product customization options
     * @param {number} productId - Product ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProductCustomizations: async (productId) => {
        try {
            const response = await apiClient.get(
                `/api/products/${productId}/customization-options`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch customization options",
            };
        }
    },

    /**
     * Search products (header search)
     * @param {string} search - Search query
     * @param {number} limit - Results limit
     * @returns {Promise<{success: boolean, data?: {products: array, total: number}, message?: string}>}
     */
    searchProducts: async (search, limit = 6) => {
        try {
            const response = await apiClient.get(
                "/api/frontend/search-header",
                {
                    params: { search, limit },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || "Search failed",
            };
        }
    },

    /**
     * Get related products
     * @param {string} slug - Product slug
     * @param {object} params - Query parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.limit - Results limit (default: 8)
     * @returns {Promise<{success: boolean, data?: array, pagination?: object, message?: string}>}
     */
    getRelatedProducts: async (slug, params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    queryParams.append(key, value);
                }
            });

            const response = await apiClient.get(
                `/api/frontend/related-products/${slug}?${queryParams.toString()}`
            );
            return {
                success: response.data.success,
                data: response.data.data,
                pagination: response.data.pagination,
                message: response.data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                pagination: { hasNextPage: false, totalCount: 0 },
                message:
                    error.response?.data?.message ||
                    "Failed to fetch related products",
            };
        }
    },

    /**
     * Get best selling products
     * @param {object} params - Query parameters
     * @param {number} params.limit - Results limit (default: 10)
     * @param {string} params.excludeSlug - Product slug to exclude (current product)
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getBestSellingProducts: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    queryParams.append(key, value);
                }
            });

            const response = await apiClient.get(
                `/api/frontend/best-selling-products?${queryParams.toString()}`
            );
            return {
                success: response.data.success,
                data: response.data.data,
                message: response.data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message:
                    error.response?.data?.message ||
                    "Failed to fetch best selling products",
            };
        }
    },
};

export default productsApi;
